import { z } from 'zod';

export const AppointmentStatusSchema = z.enum([
  'REQUESTED',
  'CONFIRMED',
  'CANCELLED',
  'DONE',
  'NO_SHOW',
]);

export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const AppointmentChannelSchema = z.enum(['APP', 'IN_PERSON']);

export type AppointmentChannel = z.infer<typeof AppointmentChannelSchema>;

export const ConsumerAppointmentItemSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  placeId: z.uuid(),
  placeName: z.string().min(1),
  status: AppointmentStatusSchema,
  channel: AppointmentChannelSchema,
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
  dropOffInstruction: z.string().min(1),
  vehiclePlate: z.string().nullable(),
  vehicleLabel: z.string().nullable(),
  proposedCivilDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  proposedDropOffTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable(),
});

export const ConsumerAppointmentsResponseSchema = z.object({
  items: z.array(ConsumerAppointmentItemSchema),
});

/** BR-APT-29: a registered vehicle belongs to the consumer — identified by plate, bookable at any bound workshop. */
export const RequestConsumerAppointmentBodySchema = z.object({
  tenantId: z.uuid(),
  placeId: z.uuid().optional(),
  plate: z.string().min(7).max(10),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  notes: z.string().max(500).optional(),
});

export const RequestConsumerAppointmentResponseSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  placeId: z.uuid(),
  status: z.enum(['REQUESTED', 'CONFIRMED']),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
  dropOffInstruction: z.string().min(1),
  vehiclePlate: z.string().nullable(),
  vehicleLabel: z.string().nullable(),
});

export const CancelAppointmentResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CANCELLED'),
});

/** BR-APT-28 */
export const ConsumerAppointmentPlacesQuerySchema = z.object({
  tenantId: z.uuid(),
});

export const ConsumerAppointmentPlaceItemSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
});

export const ConsumerAppointmentPlacesResponseSchema = z.object({
  items: z.array(ConsumerAppointmentPlaceItemSchema),
});

export const ConsumerAppointmentAvailabilityQuerySchema = z.object({
  tenantId: z.uuid(),
  placeId: z.uuid().optional(),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const AppointmentAvailabilityDaySchema = z.object({
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  open: z.boolean(),
  dropOffTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable(),
  maxVehicles: z.number().int().nonnegative(),
  booked: z.number().int().nonnegative(),
  remaining: z.number().int().nonnegative(),
});

export const ConsumerAppointmentAvailabilityResponseSchema = z.object({
  placeId: z.uuid(),
  timezone: z.string().min(1),
  days: z.array(AppointmentAvailabilityDaySchema),
});

export const PlaceAppointmentsSingleDayQuerySchema = z.strictObject({
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const PlaceAppointmentsDateRangeQuerySchema = z.strictObject({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

/** Either a single civil date (day view) or a from/to range (week kanban, BR-APT-16). */
export const PlaceAppointmentsQuerySchema = z.union([
  PlaceAppointmentsSingleDayQuerySchema,
  PlaceAppointmentsDateRangeQuerySchema,
]);

export const PlaceAppointmentItemSchema = z.object({
  id: z.uuid(),
  status: AppointmentStatusSchema,
  channel: AppointmentChannelSchema,
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
  partyId: z.uuid(),
  customerVehicleId: z.uuid().nullable(),
  vehicleMileageMeters: z.number().int().nonnegative().nullable(),
  receptionId: z.uuid().nullable(),
  customerName: z.string().min(1),
  vehiclePlate: z.string().nullable(),
  vehicleLabel: z.string().nullable(),
  checkedIn: z.boolean(),
  notes: z.string().nullable(),
  document: z.string().nullable(),
  /** Primary contact PHONE at read time (BR-APT-31); null when absent. */
  phone: z.string().nullable(),
  proposedCivilDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  proposedDropOffTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable(),
  /** Who created the appointment; `Titular` when the consumer app requested it (BR-APT-27). */
  createdByName: z.string().min(1).nullable(),
  /** Who cancelled; null when the appointment is not cancelled (BR-APT-27). */
  cancelledByName: z.string().min(1).nullable(),
});

export const PlaceAppointmentsResponseSchema = z.object({
  items: z.array(PlaceAppointmentItemSchema),
  /** Only present for the from/to range branch (BR-APT-16) — civil dates the place is open. */
  openCivilDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
});

export const ConfirmAppointmentResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CONFIRMED'),
  scheduledAt: z.iso.datetime(),
});

export const CompleteAppointmentResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('DONE'),
});

export const LinkAppointmentVehicleBodySchema = z.object({
  customerVehicleId: z.uuid(),
  vehicleMileageMeters: z.number().int().nonnegative().nullable(),
});

export const LinkAppointmentVehicleResponseSchema = z.object({
  id: z.uuid(),
  customerVehicleId: z.uuid(),
});

export const RescheduleAppointmentBodySchema = z.object({
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export const RescheduleAppointmentResponseSchema = z.object({
  id: z.uuid(),
  status: z.enum(['REQUESTED', 'CONFIRMED']),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
  proposedCivilDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  proposedDropOffTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .nullable(),
});

export const CreatePlaceAppointmentBodySchema = z.object({
  partyId: z.uuid(),
  customerVehicleId: z.uuid(),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  notes: z.string().max(500).optional(),
});

export const CreatePlaceAppointmentResponseSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  placeId: z.uuid(),
  status: z.enum(['REQUESTED', 'CONFIRMED']),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
  dropOffInstruction: z.string().min(1),
  vehiclePlate: z.string().nullable(),
  vehicleLabel: z.string().nullable(),
});

export const AcceptAppointmentRescheduleResponseSchema = z.object({
  id: z.uuid(),
  status: z.literal('CONFIRMED'),
  civilDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dropOffTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  scheduledAt: z.iso.datetime(),
});

export type ConsumerAppointmentItem = z.infer<typeof ConsumerAppointmentItemSchema>;
export type ConsumerAppointmentsResponse = z.infer<typeof ConsumerAppointmentsResponseSchema>;
export type ConsumerAppointmentPlacesQuery = z.infer<typeof ConsumerAppointmentPlacesQuerySchema>;
export type ConsumerAppointmentPlaceItem = z.infer<typeof ConsumerAppointmentPlaceItemSchema>;
export type ConsumerAppointmentPlacesResponse = z.infer<
  typeof ConsumerAppointmentPlacesResponseSchema
>;
export type RequestConsumerAppointmentBody = z.infer<typeof RequestConsumerAppointmentBodySchema>;
export type RequestConsumerAppointmentResponse = z.infer<
  typeof RequestConsumerAppointmentResponseSchema
>;
export type CancelAppointmentResponse = z.infer<typeof CancelAppointmentResponseSchema>;
export type ConsumerAppointmentAvailabilityQuery = z.infer<
  typeof ConsumerAppointmentAvailabilityQuerySchema
>;
export type ConsumerAppointmentAvailabilityResponse = z.infer<
  typeof ConsumerAppointmentAvailabilityResponseSchema
>;
export type AppointmentAvailabilityDay = z.infer<typeof AppointmentAvailabilityDaySchema>;
export type PlaceAppointmentsQuery = z.infer<typeof PlaceAppointmentsQuerySchema>;
export type PlaceAppointmentsSingleDayQuery = z.infer<typeof PlaceAppointmentsSingleDayQuerySchema>;
export type PlaceAppointmentsDateRangeQuery = z.infer<typeof PlaceAppointmentsDateRangeQuerySchema>;
export type PlaceAppointmentItem = z.infer<typeof PlaceAppointmentItemSchema>;
export type PlaceAppointmentsResponse = z.infer<typeof PlaceAppointmentsResponseSchema>;
export type ConfirmAppointmentResponse = z.infer<typeof ConfirmAppointmentResponseSchema>;
export type CompleteAppointmentResponse = z.infer<typeof CompleteAppointmentResponseSchema>;
export type LinkAppointmentVehicleBody = z.infer<typeof LinkAppointmentVehicleBodySchema>;
export type LinkAppointmentVehicleResponse = z.infer<typeof LinkAppointmentVehicleResponseSchema>;
export type RescheduleAppointmentBody = z.infer<typeof RescheduleAppointmentBodySchema>;
export type RescheduleAppointmentResponse = z.infer<typeof RescheduleAppointmentResponseSchema>;
export type CreatePlaceAppointmentBody = z.infer<typeof CreatePlaceAppointmentBodySchema>;
export type CreatePlaceAppointmentResponse = z.infer<typeof CreatePlaceAppointmentResponseSchema>;
export type AcceptAppointmentRescheduleResponse = z.infer<
  typeof AcceptAppointmentRescheduleResponseSchema
>;
